import { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/contexts/AuthContext';
import {
  applyManagerActualMinutesEdit,
  MAX_ACTUAL_MINUTES,
} from '@/lib/tickets/managerActualMinutesEdit';

interface Job {
  id: string;
  name: string;
  standardMinutes: number;
  actual_minutes?: number | null;
  job_status?: string;
}

interface EditTimersModalProps {
  ticketId: string;
  jobs: Job[];
  onUpdate?: () => void;
}

export const EditTimersModal = ({ ticketId, jobs, onUpdate }: EditTimersModalProps) => {
  const { toast } = useToast();
  // Core-100 R4: correcting measured time is a manager/owner act (server-enforced by correct_ticket_completion).
  const { canManageBilling: canCorrectMeasuredTime } = usePermissions();
  const { activeShop, user } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editedMinutes, setEditedMinutes] = useState<Record<string, number>>({});
  const [totalAllocated, setTotalAllocated] = useState(0);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      const initial: Record<string, number> = {};
      jobs.forEach(job => {
        initial[job.id] = job.actual_minutes || 0;
      });
      setEditedMinutes(initial);
      setReason('');
    }
  }, [open, jobs]);

  useEffect(() => {
    const total = Object.values(editedMinutes).reduce((sum, val) => sum + val, 0);
    setTotalAllocated(total);
  }, [editedMinutes]);

  const handleSave = async () => {
    if (!canCorrectMeasuredTime) {
      toast({ title: 'Permission Denied', description: 'Only a manager or owner can correct measured time.', variant: 'destructive' });
      return;
    }
    try {
      // SCH-1: route through the bounded + audited canonical path instead
      // of an unvalidated, unaudited, client-only direct write.
      const { audited } = await applyManagerActualMinutesEdit({
        supabase,
        ticketId,
        shopId: activeShop?.id ?? null,
        actorUserId: user?.id ?? null,
        edits: editedMinutes,
        current: Object.fromEntries(jobs.map((j) => [j.id, j.actual_minutes ?? null])),
        reason,
      });

      toast({
        title: 'Timers Updated',
        description: audited
          ? 'Correction saved and recorded (the previous time is kept). Auto-allocation disabled for this ticket.'
          : 'Correction saved. Auto-allocation disabled for this ticket.',
      });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['parking-lot-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticket-jobs'] });
      setOpen(false);
      onUpdate?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 px-2">
          <Clock className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>Manual Time Allocation</DialogTitle>
          <DialogDescription>
            Override auto-allocation by manually setting actual time for each job
          </DialogDescription>
        </DialogHeader>
        
        <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-md p-3 mb-4">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            ⚠️ Manual edits will disable automatic time allocation for this ticket
          </p>
        </div>

        <div className="space-y-4 py-4 max-h-96 overflow-y-auto">
          {jobs.map(job => (
            <div key={job.id} className="grid grid-cols-5 items-center gap-3">
              <Label className="text-right col-span-2 truncate text-sm" title={job.name}>
                {job.name}
              </Label>
              <Input
                type="number"
                min="0"
                max={MAX_ACTUAL_MINUTES}
                step="1"
                value={editedMinutes[job.id] || 0}
                onChange={(e) => setEditedMinutes(prev => ({
                  ...prev,
                  [job.id]: parseInt(e.target.value) || 0
                }))}
                className="col-span-1"
              />
              <div className="text-xs text-muted-foreground col-span-2">
                Book: {job.standardMinutes}m
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-1">
          <Label htmlFor="edit-timers-reason" className="text-sm">Reason for the correction</Label>
          <Input
            id="edit-timers-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. timer left running over lunch"
          />
        </div>

        <div className="border-t pt-3">
          <div className="flex justify-between text-sm font-medium">
            <span>Total Allocated:</span>
            <span>{totalAllocated} minutes ({(totalAllocated / 60).toFixed(1)}h)</span>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={reason.trim().length < 3}>
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};